import { type ReactNode, useId } from 'react';
import { cn } from '@/lib/utils';

/**
 * A small "i" next to a label that explains the setting on hover or keyboard focus. The text is also
 * the button's accessible description, so screen readers get it without hovering.
 */
export function InfoTip({ children, className }: { children: ReactNode; className?: string }) {
  const id = useId();
  return (
    <span className={cn('group relative inline-flex align-middle', className)}>
      <button
        type="button"
        aria-label="More info"
        aria-describedby={id}
        // Inside a <summary> a click would toggle the section; the tip is read by hovering/focusing.
        onClick={(e) => e.preventDefault()}
        className="inline-flex size-3.5 items-center justify-center rounded-full border border-current text-[9px] font-semibold leading-none text-faint hover:text-fg focus-visible:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
      >
        i
      </button>
      <span
        id={id}
        role="tooltip"
        className="pointer-events-none invisible absolute bottom-full left-1/2 z-50 mb-1.5 w-64 max-w-[80vw] -translate-x-1/2 rounded-md border border-border bg-panel px-2.5 py-2 text-xs font-normal normal-case leading-snug tracking-normal text-fg-2 opacity-0 shadow-soft transition-opacity group-focus-within:visible group-focus-within:opacity-100 group-hover:visible group-hover:opacity-100"
      >
        {children}
      </span>
    </span>
  );
}
